import React from "react";
import { useNavigate } from "react-router-dom";
import { handleNavigation } from "../../../utils/navigationUtils";
import "./card.scss";

interface CardProps {
  children?: React.ReactNode;
  disabled?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  href?: string;
  onClick?: () => void;
}

export default function Card({ 
  children, 
  disabled = false, 
  width,
  height,
  className = "",
  href,
  onClick
}: CardProps) {
  const navigate = useNavigate();
  const style: React.CSSProperties & Record<string, any> = {};
  
  if (width !== undefined) {
    style['--card-width'] = typeof width === 'number' ? `${width}px` : width;
  }
  
  if (height !== undefined) {
    style['--card-height'] = typeof height === 'number' ? `${height}px` : height;
  }

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    // If clicking on a button or link inside the card, don't trigger card click
    const target = e.target as HTMLElement;
    if (target.closest('button, a')) {
      return;
    }

    if (href) {
      handleNavigation({ href, event: e as React.MouseEvent, navigate });
    } else {
      onClick?.();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }

    // Handle Enter key for both link and button roles
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClick(e);
    }
    // Handle Space key only for button role (not for links)
    else if (e.key === ' ' && !href) {
      e.preventDefault();
      handleClick(e);
    }
  };

  const isClickable = (href || onClick) && !disabled;
  const role = href ? 'link' : (onClick ? 'button' : undefined);

  return (
    <div 
      className={`finspace-card-wrapper ${disabled ? 'finspace-card-wrapper--disabled' : ''} ${isClickable ? 'finspace-card-wrapper--clickable' : ''} ${className}`}
      role={role}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : (isClickable ? 0 : undefined)}
      style={style}
      onClick={handleClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
    >
      <div className="finspace-card">{children}</div>
    </div>
  );
}
